import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Box,
    Button,
    Card,
    TextField,
    Typography,
    Paper,
    Stack,
    IconButton,
    Divider,
    FormControlLabel,
    Switch,
    Chip,
    Alert,
    MenuItem,
    Skeleton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { httpClient } from '../../../common/httpclient';
import { standaloneFieldSx } from '../../../common/fieldStyles';
import { PLAN_SEGMENTS } from '../../../common/planSegments';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import TextAreaWithCounter from '../../../component/input/TextAreaWithCounter';

export default function PlanForm() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');

    const validationSchema = Yup.object({
        name: Yup.string()
            .required('Nome é obrigatório')
            .min(3, 'Nome deve ter pelo menos 3 caracteres'),
        description: Yup.string()
            .max(500, 'Descrição deve ter no máximo 500 caracteres'),
        value_reais: Yup.number()
            .required('Valor em reais é obrigatório')
            .min(0, 'Valor em reais deve ser maior ou igual a 0')
            .integer('Valor em reais deve ser um número inteiro'),
        value_cents: Yup.number()
            .required('Valor em centavos é obrigatório')
            .min(0, 'Valor em centavos deve ser maior ou igual a 0')
            .max(99, 'Valor em centavos deve ser menor que 100')
            .integer('Valor em centavos deve ser um número inteiro'),
        is_visible: Yup.boolean(),
        show_in_catalog: Yup.boolean(),
        external_reference: Yup.string()
            .max(100, 'Referência externa deve ter no máximo 100 caracteres'),
        ai_tokens: Yup.number()
            .min(0, 'Tokens deve ser maior ou igual a 0')
            .integer('Tokens deve ser um número inteiro'),
        whatsapp_messages: Yup.number()
            .min(0, 'Mensagens deve ser maior ou igual a 0')
            .integer('Mensagens deve ser um número inteiro'),
        payment_link_url: Yup.string()
            .max(500, 'Link deve ter no máximo 500 caracteres')
            .matches(/^(https:\/\/\S+)?$/, 'Informe uma URL válida iniciando com https://')
    });

    const handleShowSuccessSnackbar = () => {
        setOpenSnackbar(true);
        if (id) {
            setMessage('Plano atualizado com sucesso!');
        } else {
            setMessage('Plano cadastrado com sucesso!');
        }
        setSeverity('success');
    };

    const handleShowErrorSnackbar = (responseData) => {
        setOpenSnackbar(true);
        if (typeof responseData === 'object' && responseData !== null) {
            setMessage(responseData.message || responseData.id || 'Erro desconhecido');
        } else {
            setMessage(responseData || 'Erro desconhecido');
        }
        setSeverity('error');
    };

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
            value_reais: 0,
            value_cents: 0,
            is_visible: true,
            show_in_catalog: true,
            external_reference: '',
            ai_tokens: 0,
            whatsapp_messages: 0,
            payment_link_url: '',
            segment: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (id) {
                await handleUpdatePlan();
            } else {
                await handleCreatePlan();
            }
        },
    });

    const handleCreatePlan = async () => {
        try {
            setLoading(true);
            const payload = {
                name: formik.values.name,
                description: formik.values.description || null,
                value_reais: parseInt(formik.values.value_reais) || 0,
                value_cents: parseInt(formik.values.value_cents) || 0,
                is_visible: formik.values.is_visible,
                show_in_catalog: formik.values.show_in_catalog,
                external_reference: formik.values.external_reference || null,
                ai_tokens: parseInt(formik.values.ai_tokens) || 0,
                whatsapp_messages: parseInt(formik.values.whatsapp_messages) || 0,
                payment_link_url: formik.values.payment_link_url.trim() || null,
                segment: formik.values.segment || null
            };

            const response = await httpClient.post('/admin/plans', payload);
            
            if (response.status === 201 || response.status === 200) {
                handleShowSuccessSnackbar();
                setTimeout(() => {
                    navigate('/admin/plans');
                }, 1500);
            } else {
                handleShowErrorSnackbar(response.data);
            }
        } catch (error) {
            console.error('Error creating plan:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.id || 'Erro ao cadastrar plano';
            handleShowErrorSnackbar(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePlan = async () => {
        try {
            setLoading(true);
            const payload = {
                name: formik.values.name,
                description: formik.values.description || null,
                value_reais: parseInt(formik.values.value_reais) || 0,
                value_cents: parseInt(formik.values.value_cents) || 0,
                is_visible: formik.values.is_visible,
                show_in_catalog: formik.values.show_in_catalog,
                external_reference: formik.values.external_reference || null,
                ai_tokens: parseInt(formik.values.ai_tokens) || 0,
                whatsapp_messages: parseInt(formik.values.whatsapp_messages) || 0,
                payment_link_url: formik.values.payment_link_url.trim() || null,
                segment: formik.values.segment || null
            };

            const response = await httpClient.put(`/admin/plans/${id}`, payload);
            
            if (response.status === 200 || response.status === 204) {
                handleShowSuccessSnackbar();
                setTimeout(() => {
                    navigate('/admin/plans');
                }, 1500);
            } else {
                handleShowErrorSnackbar(response.data);
            }
        } catch (error) {
            console.error('Error updating plan:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.id || 'Erro ao atualizar plano';
            handleShowErrorSnackbar(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const loadPlanData = async () => {
        if (!id) return;
        
        try {
            setLoading(true);
            const response = await httpClient.get(`/admin/plans/${id}`);
            const planData = response.data;
            
            // Atualizar os valores do formulário com os dados do plano
            formik.setValues({
                name: planData.name || '',
                description: planData.description || '',
                value_reais: planData.value_reais || 0,
                value_cents: planData.value_cents || 0,
                is_visible: planData.is_visible !== undefined ? planData.is_visible : true,
                show_in_catalog: planData.show_in_catalog !== undefined ? planData.show_in_catalog : true,
                external_reference: planData.external_reference || '',
                ai_tokens: planData.ai_tokens || 0,
                whatsapp_messages: planData.whatsapp_messages || 0,
                payment_link_url: planData.payment_link_url || '',
                segment: planData.segment || ''
            });
        } catch (error) {
            console.error('Error loading plan data:', error);
            handleShowErrorSnackbar('Erro ao carregar dados do plano');
        } finally {
            setLoading(false);
        }
    };

    // Carregar dados do plano quando o componente for montado e houver um ID
    useEffect(() => {
        if (id) {
            loadPlanData();
        }
    }, [id]);

    const formatCurrency = (reais, cents) => {
        const total = parseFloat(reais || 0) + (parseFloat(cents || 0) / 100);
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(total);
    };

    const breadcrumbs = [
        {
            title: 'Planos',
            link: '/admin/plans'
        }
    ];

    // Componente Skeleton para o formulário
    const FormSkeleton = () => (
        <Box sx={{ p: 2 }}>
            <Paper 
                elevation={0}
                sx={{ 
                    mb: 3, 
                    borderRadius: 3,
                    background: theme.palette.mode === 'dark' 
                        ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                    border: theme.palette.mode === 'dark' 
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                    backdropFilter: theme.palette.mode === 'dark' ? 'blur(10px)' : 'none'
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Skeleton 
                            variant="circular" 
                            width={40} 
                            height={40} 
                            sx={{ 
                                bgcolor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255,255,255,0.2)' 
                                    : 'rgba(255,255,255,0.3)'
                            }} 
                        />
                        <Box>
                            <Skeleton 
                                variant="text" 
                                width={350} 
                                height={48} 
                                sx={{ 
                                    bgcolor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255,255,255,0.3)' 
                                        : 'rgba(255,255,255,0.8)'
                                }} 
                            />
                            <Skeleton 
                                variant="text" 
                                width={280} 
                                height={20} 
                                sx={{ 
                                    bgcolor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255,255,255,0.2)' 
                                        : 'rgba(255,255,255,0.6)'
                                }} 
                            />
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            <Paper 
                elevation={2}
                sx={{ 
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.palette.mode === 'dark' 
                        ? '0 4px 20px rgba(0,0,0,0.3)' 
                        : '0 4px 20px rgba(0,0,0,0.08)'
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="text" width={250} height={32} />
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        <Box sx={{ flex: '1 1 60%', minWidth: 0 }}>
                            <Skeleton variant="text" width="100%" height={56} />
                        </Box>
                        <Box sx={{ flex: '1 1 40%', minWidth: 0 }}>
                            <Skeleton variant="text" width="100%" height={56} />
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );

    return (
        <PageScaffold
            loading={false}
            title={id ? "Editar Plano" : "Cadastrar Novo Plano"}
            titlePageTarget={id ? "Editar" : "Novo"}
            links={breadcrumbs} 
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            {loading && id && !formik.values.name ? (
                <FormSkeleton />
            ) : (
                <Box sx={{ p: 2 }}>
                    {/* Header Section */}
                    <Paper 
                        elevation={0}
                        sx={{ 
                            mb: 3, 
                            borderRadius: 3,
                            background: theme.palette.mode === 'dark' 
                                ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                            border: theme.palette.mode === 'dark' 
                                ? '1px solid rgba(255,255,255,0.1)'
                                : 'none',
                            backdropFilter: theme.palette.mode === 'dark' ? 'blur(10px)' : 'none'
                        }}
                    >
                        <Box sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <IconButton
                                    onClick={() => navigate('/admin/plans')}
                                    sx={{ 
                                        color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                                        backgroundColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(255,255,255,0.05)' 
                                            : 'rgba(255,255,255,0.1)',
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255,255,255,0.1)'
                                                : 'rgba(255,255,255,0.2)'
                                        }
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        {id ? "Editar Plano" : "Cadastrar Novo Plano"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        {id ? "Atualize as informações do plano" : "Preencha as informações do plano"}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Paper>

                    {/* Main Form */}
                    <Paper 
                        elevation={2}
                        sx={{ 
                            borderRadius: 3,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: theme.palette.mode === 'dark' 
                                ? '0 4px 20px rgba(0,0,0,0.3)' 
                                : '0 4px 20px rgba(0,0,0,0.08)'
                        }}
                    >
                        <form onSubmit={formik.handleSubmit}>
                            {/* Basic Info Section */}
                            <Box sx={{ p: 3 }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                    <WorkspacePremiumIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Informações Básicas
                                    </Typography>
                                </Stack>

                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: 3
                                }}>
                                    <TextField
                                        fullWidth
                                        id="name"
                                        name="name"
                                        label="Nome do Plano"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.name && Boolean(formik.errors.name)}
                                        helperText={formik.touched.name && formik.errors.name}
                                        required
                                        sx={standaloneFieldSx}
                                    />

                                    <TextAreaWithCounter
                                        id="description"
                                        name="description"
                                        label="Descrição"
                                        value={formik.values.description}
                                        onChange={formik.handleChange}
                                        error={formik.touched.description && Boolean(formik.errors.description)}
                                        helperText={formik.touched.description && formik.errors.description}
                                        maxLength={500}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                            </Box>

                            <Divider />

                            {/* Value Section */}
                            <Box sx={{ 
                                p: 3, 
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255,255,255,0.02)' 
                                    : 'grey.50' 
                            }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                    <MonetizationOnIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Valor do Plano
                                    </Typography>
                                </Stack>

                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'row', 
                                    gap: 3,
                                    flexWrap: 'wrap',
                                    alignItems: 'flex-start'
                                }}>
                                    <TextField
                                        id="value_reais"
                                        name="value_reais"
                                        label="Reais"
                                        type="number"
                                        value={formik.values.value_reais}
                                        onChange={(e) => {
                                            const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                                            formik.setFieldValue('value_reais', value >= 0 ? value : 0);
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.value_reais && Boolean(formik.errors.value_reais)}
                                        helperText={formik.touched.value_reais && formik.errors.value_reais}
                                        required
                                        inputProps={{ min: 0, step: 1 }}
                                        sx={{ ...standaloneFieldSx, flex: '1 1 200px', minWidth: 0 }}
                                    />

                                    <TextField
                                        id="value_cents"
                                        name="value_cents"
                                        label="Centavos"
                                        type="number"
                                        value={formik.values.value_cents}
                                        onChange={(e) => {
                                            const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                                            formik.setFieldValue('value_cents', value >= 0 && value < 100 ? value : formik.values.value_cents);
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.value_cents && Boolean(formik.errors.value_cents)}
                                        helperText={formik.touched.value_cents && formik.errors.value_cents}
                                        required
                                        inputProps={{ min: 0, max: 99, step: 1 }}
                                        sx={{ ...standaloneFieldSx, flex: '1 1 200px', minWidth: 0 }}
                                    />

                                    <Box sx={{ 
                                        flex: '1 1 100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(255,255,255,0.05)' 
                                            : 'rgba(0,0,0,0.02)',
                                        border: '1px dashed',
                                        borderColor: theme.palette.divider
                                    }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body2" color="text.secondary">
                                                Valor Total:
                                            </Typography>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: 700,
                                                    color: 'success.main'
                                                }}
                                            >
                                                {formatCurrency(formik.values.value_reais, formik.values.value_cents)}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            {/* Configuration Section */}
                            <Box sx={{ 
                                p: 3, 
                                backgroundColor: theme.palette.background.paper
                            }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                    <SettingsIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Configurações
                                    </Typography>
                                </Stack>

                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: 3
                                }}>
                                    <FormControlLabel
                                        control={
                                            <Switch 
                                                checked={formik.values.is_visible}
                                                onChange={(e) => {
                                                    formik.setFieldValue('is_visible', e.target.checked);
                                                }}
                                                size="medium"
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <VisibilityIcon 
                                                    sx={{ 
                                                        color: formik.values.is_visible 
                                                            ? 'success.main' 
                                                            : 'text.secondary' 
                                                    }} 
                                                />
                                                <Typography variant="body1">
                                                    Plano Visível
                                                </Typography>
                                                <Chip 
                                                    label={formik.values.is_visible ? "Visível" : "Oculto"} 
                                                    size="small" 
                                                    color={formik.values.is_visible ? "success" : "default"}
                                                    variant={formik.values.is_visible ? "filled" : "outlined"}
                                                />
                                                {!formik.values.is_visible && (
                                                    <Alert severity="info" sx={{ py: 0, px: 1, fontSize: '0.75rem' }}>
                                                        O plano ficará oculto no admin e não poderá ser selecionado
                                                    </Alert>
                                                )}
                                            </Box>
                                        }
                                    />

                                    <Box>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formik.values.show_in_catalog}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('show_in_catalog', e.target.checked);
                                                    }}
                                                    size="medium"
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body1">
                                                        Exibir no catálogo da landing page
                                                    </Typography>
                                                    <Chip
                                                        label={formik.values.show_in_catalog ? "Exibido" : "Customizado"}
                                                        size="small"
                                                        color={formik.values.show_in_catalog ? "success" : "default"}
                                                        variant={formik.values.show_in_catalog ? "filled" : "outlined"}
                                                    />
                                                </Box>
                                            }
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                                            Desmarque para criar plano customizado para um cliente
                                        </Typography>
                                    </Box>

                                    <TextField
                                        select
                                        fullWidth
                                        id="segment"
                                        name="segment"
                                        label="Segmento"
                                        value={formik.values.segment}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.segment && Boolean(formik.errors.segment)}
                                        helperText={formik.touched.segment && formik.errors.segment || 'Segmento que verá este plano ao assinar. "Todos" exibe para qualquer segmento'}
                                        sx={standaloneFieldSx}
                                    >
                                        <MenuItem value="">Todos os segmentos</MenuItem>
                                        {PLAN_SEGMENTS.map((s) => (
                                            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        fullWidth
                                        id="external_reference"
                                        name="external_reference"
                                        label="Referência Externa"
                                        value={formik.values.external_reference}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.external_reference && Boolean(formik.errors.external_reference)}
                                        helperText={formik.touched.external_reference && formik.errors.external_reference || 'Referência externa opcional (ex: código do gateway de pagamento)'}
                                        sx={standaloneFieldSx}
                                    />

                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, flexWrap: 'wrap' }}>
                                        <TextField
                                            id="ai_tokens"
                                            name="ai_tokens"
                                            label="Tokens de IA"
                                            type="number"
                                            value={formik.values.ai_tokens}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.ai_tokens && Boolean(formik.errors.ai_tokens)}
                                            helperText={formik.touched.ai_tokens && formik.errors.ai_tokens || 'Franquia de tokens do assistente de IA (0 = não incluso)'}
                                            inputProps={{ min: 0, step: 1 }}
                                            sx={{ ...standaloneFieldSx, flex: '1 1 240px', minWidth: 0 }}
                                        />
                                        <TextField
                                            id="whatsapp_messages"
                                            name="whatsapp_messages"
                                            label="Mensagens de WhatsApp por mês"
                                            type="number"
                                            value={formik.values.whatsapp_messages}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.whatsapp_messages && Boolean(formik.errors.whatsapp_messages)}
                                            helperText={formik.touched.whatsapp_messages && formik.errors.whatsapp_messages || 'Franquia mensal de envios (0 = não incluso)'}
                                            inputProps={{ min: 0, step: 1 }}
                                            sx={{ ...standaloneFieldSx, flex: '1 1 240px', minWidth: 0 }}
                                        />
                                    </Box>

                                    <TextField
                                        fullWidth
                                        id="payment_link_url"
                                        name="payment_link_url"
                                        label="Link de pagamento"
                                        value={formik.values.payment_link_url}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.payment_link_url && Boolean(formik.errors.payment_link_url)}
                                        helperText={formik.touched.payment_link_url && formik.errors.payment_link_url || 'Payment Link da plataforma (ex.: Stripe), https://... Usado no botão "Assinar" da landing'}
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                            </Box>

                            {/* Action Buttons */}
                            <Box sx={{ 
                                p: 3, 
                                backgroundColor: theme.palette.background.paper,
                                borderTop: '1px solid',
                                borderColor: theme.palette.divider
                            }}>
                                <Stack direction="row" spacing={2} justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        startIcon={<CancelIcon />}
                                        onClick={() => navigate('/admin/plans')}
                                        disabled={loading}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            px: 3,
                                            py: 1.5
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        disabled={loading}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            px: 3,
                                            py: 1.5,
                                            boxShadow: theme.palette.mode === 'dark' 
                                                ? '0 4px 12px rgba(0,0,0,0.3)' 
                                                : '0 4px 12px rgba(0,0,0,0.15)'
                                        }}
                                    >
                                        {loading ? 'Salvando...' : (id ? 'Atualizar Plano' : 'Salvar Plano')}
                                    </Button>
                                </Stack>
                            </Box>
                        </form>
                    </Paper>
                </Box>
            )}
        </PageScaffold>
    );
}

